import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Lock } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Card, CardContent, Badge, Input, Checkbox,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import { getConteo, guardarLineasConteo, cerrarConteo } from '../../../lib/supabase'
import { lineDiferencia } from '../../../lib/inventory'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'

const ESTADO_LABELS = {
  abierto: 'Abierto',
  cerrado: 'Cerrado',
}

const toInput = (v) => (v === null || v === undefined ? '' : String(v))

const ConteoDetalle = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [count, setCount] = useState(null)
  const [lines, setLines] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [closing, setClosing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await getConteo(id)
    setCount(data || null)
    setLines(
      (data?.lines || []).map((l) => ({
        ...l,
        cantidad_real: toInput(l.cantidad_real),
        ajustar: !!l.ajustar,
      }))
    )
    setLoading(false)
  }, [id])

  useEffect(() => { load() }, [load])

  const cerrado = count?.estado === 'cerrado'

  const updateLine = (lineId, patch) => {
    setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...patch } : l)))
  }

  const ajustablesCount = lines.filter(
    (l) => l.ajustar && l.cantidad_real !== '' && lineDiferencia(l) !== 0
  ).length

  const payload = () =>
    lines.map((l) => ({ id: l.id, cantidad_real: l.cantidad_real, ajustar: l.ajustar }))

  const handleGuardar = async () => {
    setSaving(true)
    const { error } = await guardarLineasConteo(payload())
    setSaving(false)
    if (error) {
      await alert('Error', error.message || 'No se pudieron guardar las cantidades')
      return
    }
    await load()
  }

  const handleCerrar = async () => {
    const n = ajustablesCount
    const ok = await confirm(
      'Cerrar conteo',
      n > 0
        ? `Se aplicarán ${n} ajuste(s) de inventario según las diferencias marcadas. ` +
            'Esto generará movimientos de ajuste y el conteo quedará en solo lectura.'
        : 'No hay líneas marcadas para ajustar. El conteo se cerrará sin generar ajustes y quedará en solo lectura.',
      { confirmText: 'Cerrar conteo' }
    )
    if (!ok) return
    setClosing(true)
    const saveRes = await guardarLineasConteo(payload())
    if (saveRes.error) {
      setClosing(false)
      await alert('Error', saveRes.error.message || 'No se pudieron guardar las cantidades')
      return
    }
    const { data, error } = await cerrarConteo(id)
    setClosing(false)
    if (error) {
      await alert('Error', error.message || 'No se pudo cerrar el conteo')
      return
    }
    await alert('Conteo cerrado', `Conteo cerrado, ${data?.ajustados ?? 0} ajuste(s) aplicado(s).`)
    await load()
  }

  const etiqueta = count?.nombre?.trim() || (count?.fecha ? `Conteo ${count.fecha}` : 'Conteo')
  const subtitle = count
    ? `${ESTADO_LABELS[count.estado] || count.estado}${count.fecha ? ` · ${count.fecha}` : ''}`
    : 'Detalle de conteo'

  return (
    <FeaturedLayout title={etiqueta} subtitle={subtitle} refreshAction={load}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/inventario/conteos')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Conteos
        </Button>
        {count && (
          <Badge variant={cerrado ? 'secondary' : 'default'} className="text-xs">
            {cerrado && <Lock className="h-3 w-3 mr-1" />}
            {ESTADO_LABELS[count.estado] || count.estado}
          </Badge>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : !count ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No se encontró el conteo</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Movil: esta pantalla se usa DE PIE EN LA BODEGA contando con el telefono
              en una mano, asi que es su dispositivo principal, no un fallback. Cada
              articulo es un bloque con el campo de captura grande y la diferencia
              calculandose al lado. */}
          <Card className="sm:hidden divide-y">
            {lines.map((line) => {
              const detalle = [line.item?.talla, line.item?.modelo].filter(Boolean).join(' · ')
              const tieneReal = line.cantidad_real !== '' && line.cantidad_real !== null
              const dif = tieneReal ? lineDiferencia(line) : null
              const difColor =
                dif === null ? 'text-muted-foreground'
                  : dif === 0 ? 'text-emerald-600'
                    : 'text-amber-600'
              return (
                <div key={line.id} className="p-4 space-y-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{line.item?.nombre || 'Artículo'}</p>
                    {detalle && <p className="text-xs text-muted-foreground truncate">{detalle}</p>}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-xs text-muted-foreground shrink-0">
                      Esperado
                      <span className="ml-1 font-medium text-foreground tabular-nums">
                        {line.cantidad_esperada ?? 0}
                      </span>
                    </div>
                    {cerrado ? (
                      <span className="text-sm font-semibold tabular-nums">
                        {tieneReal ? line.cantidad_real : '—'}
                      </span>
                    ) : (
                      <Input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        aria-label={`Cantidad contada de ${line.item?.nombre || 'artículo'}`}
                        placeholder="Contado"
                        className="h-11 w-24 text-center text-base"
                        value={line.cantidad_real}
                        onChange={(e) =>
                          updateLine(line.id, {
                            cantidad_real: e.target.value,
                            ajustar: e.target.value === '' ? false : line.ajustar,
                          })
                        }
                      />
                    )}
                    <span className={`text-sm font-semibold tabular-nums ${difColor}`}>
                      {dif === null ? '—' : dif > 0 ? `+${dif}` : dif}
                    </span>
                  </div>

                  {!cerrado && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={line.ajustar}
                        disabled={!tieneReal}
                        onCheckedChange={(v) => updateLine(line.id, { ajustar: !!v })}
                      />
                      Ajustar existencia al cerrar
                    </label>
                  )}
                </div>
              )
            })}
          </Card>

          <Card className="hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artículo</TableHead>
                  <TableHead className="text-right">Esperado</TableHead>
                  <TableHead className="text-right">Contado</TableHead>
                  <TableHead className="text-right">Diferencia</TableHead>
                  <TableHead className="text-center w-20">Ajustar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) => {
                  const detalle = [line.item?.talla, line.item?.modelo].filter(Boolean).join(' · ')
                  const tieneReal = line.cantidad_real !== '' && line.cantidad_real !== null
                  const dif = tieneReal ? lineDiferencia(line) : null
                  const difColor =
                    dif === null ? 'text-muted-foreground'
                      : dif === 0 ? 'text-emerald-600'
                        : 'text-amber-600'
                  return (
                    <TableRow key={line.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{line.item?.nombre || 'Artículo'}</span>
                          {detalle && <p className="text-xs text-muted-foreground">{detalle}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {line.cantidad_esperada ?? 0}
                      </TableCell>
                      <TableCell className="text-right">
                        {cerrado ? (
                          <span>{tieneReal ? line.cantidad_real : '—'}</span>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            inputMode="numeric"
                            className="h-8 w-24 ml-auto text-right"
                            value={line.cantidad_real}
                            onChange={(e) =>
                              updateLine(line.id, {
                                cantidad_real: e.target.value,
                                ajustar: e.target.value === '' ? false : line.ajustar,
                              })
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${difColor}`}>
                        {dif === null ? '—' : dif > 0 ? `+${dif}` : dif}
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={line.ajustar}
                          disabled={cerrado || !tieneReal}
                          onCheckedChange={(v) => updateLine(line.id, { ajustar: !!v })}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>

          {!cerrado && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{ajustablesCount}</span> línea(s) se ajustarán al cerrar
              </span>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={handleGuardar} disabled={saving || closing}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button onClick={handleCerrar} disabled={saving || closing}>
                  {closing ? 'Cerrando...' : 'Cerrar conteo'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default ConteoDetalle
